import React, { type ReactNode } from 'react'
import { usePermissions } from '../../hooks/usePermissions'
import { UserRole, PermissionResource, PermissionAction } from '../../types'

interface PermissionGateProps {
  children: ReactNode
  requiredRole?: UserRole
  requiredRoles?: UserRole[]
  requiredPermission?: {
    resource: PermissionResource
    action: PermissionAction
  }
  requiredPermissions?: Array<{
    resource: PermissionResource
    action: PermissionAction
  }>
  requireAll?: boolean // For multiple permissions/roles, require all (AND) or any (OR)
  fallback?: ReactNode
  showFallback?: boolean
}

/**
 * PermissionGate component for conditional rendering based on user permissions
 * Provides fine-grained control over component visibility
 */
export const PermissionGate: React.FC<PermissionGateProps> = ({
  children,
  requiredRole,
  requiredRoles,
  requiredPermission,
  requiredPermissions,
  requireAll = true,
  fallback = null,
  showFallback = true,
}) => {
  const { hasRole, hasPermission } = usePermissions()

  // Check single role requirement
  if (requiredRole && !hasRole(requiredRole)) {
    return showFallback ? <>{fallback}</> : null
  }

  // Check multiple roles requirement
  if (requiredRoles && requiredRoles.length > 0) {
    const roleCheck = requireAll
      ? requiredRoles.every(role => hasRole(role))
      : requiredRoles.some(role => hasRole(role))

    if (!roleCheck) {
      return showFallback ? <>{fallback}</> : null
    }
  }

  // Check single permission requirement
  if (requiredPermission) {
    const { resource, action } = requiredPermission
    if (!hasPermission(resource, action)) {
      return showFallback ? <>{fallback}</> : null
    }
  }

  // Check multiple permissions requirement
  if (requiredPermissions && requiredPermissions.length > 0) {
    const permissionCheck = requireAll
      ? requiredPermissions.every(({ resource, action }) =>
          hasPermission(resource, action)
        )
      : requiredPermissions.some(({ resource, action }) =>
          hasPermission(resource, action)
        )

    if (!permissionCheck) {
      return showFallback ? <>{fallback}</> : null
    }
  }

  return <>{children}</>
}

/**
 * Higher-order component for wrapping components with permission checks
 */
export function withPermissionGate<P extends object>(
  Component: React.ComponentType<P>,
  gateProps: Omit<PermissionGateProps, 'children'>
) {
  return function GatedComponent(props: P) {
    return (
      <PermissionGate {...gateProps}>
        <Component {...props} />
      </PermissionGate>
    )
  }
}

/**
 * Convenience components for common permission checks
 */
export const AdminOnly: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate requiredRole={UserRole.ADMINISTRADOR} fallback={fallback}>
    {children}
  </PermissionGate>
)

export const EditorOnly: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate requiredRole={UserRole.EDITOR} fallback={fallback}>
    {children}
  </PermissionGate>
)

export const ViewerOnly: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate requiredRole={UserRole.VISUALIZADOR} fallback={fallback}>
    {children}
  </PermissionGate>
)
export const UserManagementGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredPermission={{
      resource: PermissionResource.USERS,
      action: PermissionAction.READ,
    }}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

export const RoleManagementGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredPermission={{
      resource: PermissionResource.ROLES,
      action: PermissionAction.READ,
    }}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

export const CreateUserGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredPermission={{
      resource: PermissionResource.USERS,
      action: PermissionAction.CREATE,
    }}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

export const EditUserGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredPermission={{
      resource: PermissionResource.USERS,
      action: PermissionAction.UPDATE,
    }}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

export const DeleteUserGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredPermission={{
      resource: PermissionResource.USERS,
      action: PermissionAction.DELETE,
    }}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

export const CreateSolicitudGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredPermission={{
      resource: PermissionResource.SOLICITUDES,
      action: PermissionAction.CREATE,
    }}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

export const EvaluatePostulacionGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredPermission={{
      resource: PermissionResource.POSTULACIONES,
      action: PermissionAction.EVALUATE,
    }}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

export const ReportsGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredPermission={{
      resource: PermissionResource.REPORTS,
      action: PermissionAction.VIEW,
    }}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

export const QualifyCandidateGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredPermission={{
      resource: PermissionResource.POSTULACIONES,
      action: PermissionAction.QUALIFY,
    }}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

export const BulkQualifyCandidateGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredPermission={{
      resource: PermissionResource.POSTULACIONES,
      action: PermissionAction.BULK_QUALIFY,
    }}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

export const EditorOrAdminGate: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => (
  <PermissionGate
    requiredRoles={[UserRole.EDITOR, UserRole.ADMINISTRADOR]}
    requireAll={false}
    fallback={fallback}
  >
    {children}
  </PermissionGate>
)

/**
 * Utility component for showing different content based on user role
 */
export const RoleBasedContent: React.FC<{
  adminContent?: ReactNode
  editorContent?: ReactNode
  viewerContent?: ReactNode
  defaultContent?: ReactNode
}> = ({ adminContent, editorContent, viewerContent, defaultContent }) => {
  const { isAdmin, isEditor, isViewer } = usePermissions()

  if (isAdmin() && adminContent) {
    return <>{adminContent}</>
  }

  if (isEditor() && editorContent) {
    return <>{editorContent}</>
  }

  if (isViewer() && viewerContent) {
    return <>{viewerContent}</>
  }

  return <>{defaultContent}</>
}

/**
 * Component for showing loading state while permissions are being checked
 */
export const PermissionLoader: React.FC<{
  children: ReactNode
  fallback?: ReactNode
}> = ({ children, fallback }) => {
  const { user } = usePermissions()

  if (!user) {
    return <>{fallback || <div>Cargando permisos...</div>}</>
  }

  return <>{children}</>
}
